import jwtDefaultConfig from './jwtDefaultConfig'
import store from '../../../store'
import {getMfaState} from '@/auth/mfa'

export default class JwtService {
    // Will be used by this service for making API calls
    axiosIns = null

    // jwtConfig <= Will be used by this service
    jwtConfig = {...jwtDefaultConfig}

    // For Refreshing Token
    isAlreadyFetchingAccessToken = false

    // For Refreshing Token
    subscribers = []

    onAuthenticationFailedEvents = []

    constructor(axiosIns, jwtOverrideConfig) {
        this.axiosIns = axiosIns
        this.jwtConfig = {...this.jwtConfig, ...jwtOverrideConfig}

        // Request Interceptor
        this.axiosIns.interceptors.request.use(
            config => {
                // Get token from localStorage
                const accessToken = this.getToken()

                // If token is present add it to request's Authorization Header
                if (accessToken) {
                    // eslint-disable-next-line no-param-reassign
                    if (config.url !== this.jwtConfig.refreshEndpoint) {
                        config.headers.Authorization = `${this.jwtConfig.tokenType} ${accessToken}`
                    }
                }
                return config
            },
            error => Promise.reject(error),
        )

        // Add request/response interceptor
        this.axiosIns.interceptors.response.use(
            response => response,
            error => {
                // const { config, response: { status } } = error
                const {config, response} = error
                const originalRequest = config

                if (response && response.status === 401) {
                    if (!this.isAlreadyFetchingAccessToken) {
                        if (this.getRefreshToken()) {
                            this.isAlreadyFetchingAccessToken = true
                            return this.refreshToken().then(r => {
                                this.isAlreadyFetchingAccessToken = false

                                // Update accessToken in localStorage
                                this.setToken(r.data.token)
                                this.setRefreshToken(r.data.refresh_token)

                                store.dispatch('accessTokenRefreshed');

                                const refreshComplete = getMfaState()
                                    ? Promise.resolve()
                                    : store.dispatch('selectOrganization', store.state.user.organization)

                                return refreshComplete
                                    .then(() => {
                                        this.onAccessTokenFetched(r.data.token);

                                        originalRequest.headers.Authorization = `${this.jwtConfig.tokenType} ${r.data.token}`
                                        return this.axiosIns(originalRequest);
                                    })
                                ;
                            });
                        } else {
                            return Promise.reject(error);
                        }
                    } else if (response.config.url.indexOf(this.jwtConfig.refreshEndpoint) >= 0) {
                        this.subscribers = [];
                        if (this.onAuthenticationFailed) {
                            this.onAuthenticationFailedEvents.forEach((cb) => cb('Authentication failed'));
                        }
                        return error;
                    }
                    return new Promise(resolve => {
                        this.addSubscriber(accessToken => {
                            // Make sure to assign accessToken according to your response.
                            // Check: https://pixinvent.ticksy.com/ticket/2413870
                            // Change Authorization header
                            originalRequest.headers.Authorization = `${this.jwtConfig.tokenType} ${accessToken}`
                            resolve(this.axiosIns(originalRequest))
                        })
                    })
                }
                return Promise.reject(error)
            },
        )
    }

    onAccessTokenFetched(accessToken) {
        this.subscribers = this.subscribers.filter(callback => callback(accessToken))
    }

    addSubscriber(callback) {
        this.subscribers.push(callback)
    }

    getToken() {
        return localStorage.getItem(this.jwtConfig.storageTokenKeyName)
    }

    getRefreshToken() {
        return localStorage.getItem(this.jwtConfig.storageRefreshTokenKeyName)
    }

    hasToken() {
        return null !== this.getToken() && null !== this.getRefreshToken();
    }

    setToken(value) {
        localStorage.setItem(this.jwtConfig.storageTokenKeyName, value)
    }

    setRefreshToken(value) {
        localStorage.setItem(this.jwtConfig.storageRefreshTokenKeyName, value)
    }

    login(...args) {
        return this.axiosIns.post(this.jwtConfig.loginEndpoint, ...args)
    }

    register(...args) {
        return this.axiosIns.post(this.jwtConfig.registerEndpoint, ...args)
    }

    clear() {
        localStorage.removeItem(this.jwtConfig.storageTokenKeyName);
        localStorage.removeItem(this.jwtConfig.storageRefreshTokenKeyName);
    }

    refreshToken() {
        return this.axiosIns.post(this.jwtConfig.refreshEndpoint, {
            refresh_token: this.getRefreshToken(),
        })
    }

    onAuthenticationFailed(cb) {
        this.onAuthenticationFailedEvents.push(cb);
    }
}
